<template>
  <CenterContent size="xs12">
    <PermissionError v-if="showPermissionError" />
    <template v-else>
      <Alert :type="response.variant" :message="response.message" />
      <Alert loading :value="percentage" :type="alert.variant" :message="alert.message" />
    <DataTable
      filterable
      header-group-by
      title="Recent Actions"
      :datatable-items="dataTableItems"
      :loading="response.loading"
      :custom-options="dataTableOptions"
      :custom-header-values="headerValues"
      :custom-actions="customActions"
      :custom-loading-type="customLoadingType"
      :custom-header-ignore="ignoreHeader">

      <template v-slot:item.editor="{ item }">
        <div class="d-flex align-center">
          <span v-if="isHeadEditorAction(item)" class="editor-badge head-editor you-badge">
            <i class="bx bx-user"></i>
            YOU
          </span>
          <TooltipLabel v-else-if="isSubEditorAction(item)" top :label="'This is your sub editor'">
            <span class="editor-badge sub-editor">
              <i class="bx bx-user-circle"></i>
              {{ getEditorDisplayName(item) }}
            </span>
          </TooltipLabel>
          <span v-else>
            {{ item.editor }}
          </span>
        </div>
      </template>

      <template v-slot:item.request_type="{ item }">
        <button @click="onGoToPageClick(item)" text class="text-left" style="justify-content: flex-start; text-align: left;">
          {{ item.request_type }}
          <i class="bx bx-link-external" />
        </button>
      </template>
      
      <template v-slot:item.requested="{ item }">
        <LoadingCard v-if="item.data === '###'" :classObject="['px-4', 'py-0']" :loading="item.data === '###'" type="list-item">
          {{ item.data }}
        </LoadingCard>
        <TooltipLabel v-else-if="item.data_tooltip && item.data_tooltip !== ''" top :label="item.data_tooltip">
          {{ item.data }}
        </TooltipLabel>
        <span v-else>{{ item.data }}</span>
      </template>

      <template v-slot:item.result="{ item }">
        <LoadingCard v-if="item.message === '###'" :classObject="['px-4', 'py-0']" :loading="item.message === '###'" type="list-item">
          {{ item.message }}
        </LoadingCard>
        <TooltipLabel v-else-if="item.message_tooltip && item.message_tooltip !== ''" top :label="item.message_tooltip">
          {{ item.message }}
        </TooltipLabel>
        <span v-else>{{ item.message }}</span>
      </template>

      <template v-slot:item.status="{ item }">
        <span>{{ (item.display_status !== undefined && item.display_status !== null && item.display_status !== '') ? item.display_status : (item.status || '-') }}</span>
      </template>

      <template v-slot:item.time="{ item }">
        <TooltipLabel top :label="getTimeTooltip(item)">
          <span class="text--secondary" style="font-size: 11px;">
            {{ fromNow(item.time) }}
          </span>
        </TooltipLabel>
      </template>
    </DataTable>
    </template>
  </CenterContent>
</template>

<script>
import CenterContent from "@/base_lib/components/container/CenterContent"
import DataTable from "@/base_lib/components/container/DataTable"
import PageMixin from "@/base_lib/data/mixins/pageMixin"
import RequestService from "@/services/RequestService"
import {mapGetters} from "vuex"
import Select from "@/base_lib/components/form/Select"
import Alert from "@/base_lib/components/view/Alert"
import Button from "@/base_lib/components/form/Button"
import TooltipLabel from "@/base_lib/components/container/TooltipLabel"
import getQueryParams from "@/data/view/getQueryParams"
import GenerateServices from "@/services/GenerateServices"
import {camelizeKeys} from "@/base_lib/helper/utils"
import LoadingCard from "@/base_lib/components/view/LoadingCard.vue"
import moment from "moment"
import PermissionError from "@/view/components/PermissionError.vue"

export default {
  name: "Welcome",
  components: {PermissionError, LoadingCard, TooltipLabel, Button, DataTable, CenterContent, Select, Alert },
  mixins: [PageMixin],
  computed: {
    ...mapGetters({
      account: 'auth/account',
    }),
    showPermissionError() {
      return this.$route.query.permissionError === 'true'
    },
    customActions() {
      return [
        {
          title: "Download",
          click: this.onDownloadClick,
          isRendered: (item) => {
            return item.status.toLowerCase() === "completed" && !!item.custom_data.cloakify === false && !this.noDownloadTypes.includes(item.type)
          }
        },
        {
          title: "Standard",
          click: this.onDownloadClick,
          isRendered: (item) => {
            return item.status.toLowerCase() === "completed" && !!item.custom_data.cloakify && !this.noDownloadTypes.includes(item.type)
          }
        },
        {
          title: "Cloakify",
          click: this.onCloakifyClick,
          isRendered: (item) => {
            return item.status.toLowerCase() === "completed" && !!item.custom_data.cloakify && !this.noDownloadTypes.includes(item.type)
          }
        },
        {
          title: "View Tickets",
          click: this.onGoToPageClick,
          isRendered: (item) => {
            return item.status.toLowerCase() === "completed" && this.refreshTypes.includes(item.type) && item.type !== 'REFRESH_CREDITS'
          }
        },
        {
          title: "View",
          click: this.onGoToPageClick,
          isRendered: (item) => {
            return item.status.toLowerCase() === "completed" && item.type === 'REFRESH_CREDITS'
          }
        },
        {
          title: "Retry",
          click: this.onRetryClick,
          isRendered: (item) => {
            const displayStatus = item.display_status || item.status
            const statusLower = displayStatus.toLowerCase()
            return (statusLower === 'failed' || statusLower === 'abandoned') && !this.noRetryTypes.includes(item.type)
          }
        }
      ]
    },
    customLoadingType() {
      return "table-heading, table-thead, table-tbody, table-tfoot"
    },
  },
  data: () => ({
    localItems: [],
    dataTableItems: [],
    ignoreHeader: ['type', '_search'],
    headerValues: ['editor', 'request_type', 'requested', 'status', 'result', 'time', 'action', '_search'],
    dataTableOptions: {
      page: 1,
      itemsPerPage: 30,
      itemsPerPageOptions: [30, 50, 100],
      sort: ["time"],
      desc: true,
    },
    editors: [],
    percentage: null,

    // Data
    noRetryTypes: ["CREATE_BALLPARK_ACCOUNT"],
    noDownloadTypes: ["GENERATE_QR_ASYNC", "INVENTORY_API_ASYNC", "INVENTORY_APP_ASYNC", "INVENTORY_WEB_ASYNC", "RESET_ACCOUNT_PASSWORD", "BALLPARK_VERIFY_EMAIL", "CREATE_BALLPARK_ACCOUNT", "SIDE_DMS", "REFRESH_CREDITS", "PACIOLAN_SCRAPE", "SEATGEEK_SCRAPE", "SEATGEEK_OFFICIAL_API"],
    refreshTypes: ["GENERATE_QR_ASYNC", "INVENTORY_API_ASYNC", "INVENTORY_APP_ASYNC", "INVENTORY_WEB_ASYNC", "REFRESH_CREDITS", "SIDE_DMS", "PACIOLAN_SCRAPE", "SEATGEEK_SCRAPE", "SEATGEEK_OFFICIAL_API"]
  }),
  methods: {
    fetch() {
      // Only set loading if we don't have items already (from light fetch)
      if (this.dataTableItems.length === 0) {
        this.response.loading = true
      }
      const isAdmin = this.account?.permissions?.includes('admin') || false

      RequestService.history([], isAdmin).then(response => {
        this.decorateItems(response)
      }).catch(error => {
        if (typeof error.networkError === 'function') {
          this.response = error.networkError?.(error)
        } else {
          this.response = this.returnCustomError(error.toString())
        }
      }).finally(() => {
        this.response.loading = false
      })
    },
    fetchLight() {
      this.response.loading = true
      const isAdmin = this.account?.permissions?.includes('admin') || false

      RequestService.historyLight([], isAdmin).then(response => {
        this.decorateLightItems(response)
      }).catch(error => {
        if (typeof error.networkError === 'function') {
          this.response = error.networkError?.(error)
        } else {
          this.response = this.returnCustomError(error.toString())
        }
      }).finally(() => {
        this.response.loading = false
        this.fetch()
      })
    },
    generateSearchString(obj) {
      if (!obj) return ''
      
      const seen = new Set()
      const extractValues = (item) => {
        if (!item || seen.has(item)) return ''
        
        if (typeof item !== 'object') {
          return String(item).toLowerCase() + ' '
        }
        
        seen.add(item)
        let str = ''
        if (Array.isArray(item)) {
          for (let i = 0; i < item.length; i++) {
            str += extractValues(item[i])
          }
        } else {
          for (const key in item) {
            if (Object.prototype.hasOwnProperty.call(item, key)) {
              str += extractValues(item[key])
            }
          }
        }
        return str
      }

      return extractValues(obj)
    },
    getEditorLabel(editor) {
      if (!editor) return "-"
      
      // If not head editor, return normal editor label
      if (!this.account?.is_head) {
        const data = editor.data ?? {}
        const firstName = editor.first_name ?? data.first_name ?? ""
        const lastName = editor.last_name ?? data.last_name ?? ""
        const fullName = [firstName, lastName].filter(Boolean).join(" ").trim()
        const email = data.email ?? editor.email
        const username = editor.username ?? data.username

        if (email) return email
        if (fullName.length > 0) return fullName
        if (username) return username
        return this.account.username || "-"
      }
      
      // Head editor case: check if this is the head editor themselves
      const accountEditorId = String(this.account.editor_id || this.account.id)
      const editorId = String(editor.editor_id || editor.id)
      
      if (accountEditorId === editorId) {
        // Try to get email for head editor
        const accountData = this.account.data || {}
        const email = accountData.email || 
                     this.account.username || 
                     accountData.editor_user ||
                     accountData.editor_name
        
        if (email) {
          return email
        }
        
        // Fallback: use username if available
        if (this.account.username) {
          return this.account.username
        }
        
        return this.account.username || "-"
      }
      
      // Find child editor
      const childEditors = Array.isArray(this.account.child_editors) ? this.account.child_editors : []
      const childEditor = childEditors.find((child) => {
        return String(child.editor_id) === String(editorId)
      })
      
      if (childEditor) {
        return childEditor.editor_name || childEditor.editor_user || childEditor.editor_id
      }
      
      // Fallback if child editor not found
      return this.account.username || "-"
    },
    isHeadEditorAction(item) {
      if (!this.account?.is_head || !item.editor_object) return false
      const accountEditorId = String(this.account.editor_id || this.account.id)
      const editorId = String(item.editor_object.editor_id || item.editor_object.id)
      return accountEditorId === editorId
    },
    isSubEditorAction(item) {
      if (!this.account?.is_head || !item.editor_object) return false
      const accountEditorId = String(this.account.editor_id || this.account.id)
      const editorId = String(item.editor_object.editor_id || item.editor_object.id)
      if (accountEditorId === editorId) return false
      
      const childEditors = Array.isArray(this.account.child_editors) ? this.account.child_editors : []
      return childEditors.some((child) => String(child.editor_id) === String(editorId))
    },
    getEditorDisplayName(item) {
      if (!item.editor_object) return item.editor || "-"
      
      // Head editor
      if (this.isHeadEditorAction(item)) {
        // Try to get email for head editor
        // account.username is typically the login email
        // account.data might have email or editor_user (which is usually email)
        const accountData = this.account.data || {}
        const email = accountData.email || 
                     this.account.username || 
                     accountData.editor_user ||
                     accountData.editor_name
        
        if (email) {
          return email
        }
        
        // Fallback: use username if available
        if (this.account.username) {
          return this.account.username
        }
        
        return "Head Editor"
      }
      
      // Sub editor
      if (this.isSubEditorAction(item)) {
        const childEditors = Array.isArray(this.account.child_editors) ? this.account.child_editors : []
        const editorId = String(item.editor_object.editor_id || item.editor_object.id)
        const childEditor = childEditors.find((child) => String(child.editor_id) === String(editorId))
        if (childEditor) {
          return childEditor.editor_name || childEditor.editor_user || childEditor.editor_id
        }
      }
      
      return item.editor || "-"
    },
    decorateLightItems(results) {
      let items = results.data.data.list.map(item => {
        // Debug: Check if display_status exists
        if (item.request_status === 'requested' && !item.display_status) {
          console.warn('Missing display_status for requested item:', item)
        }
        
        return {
          ID: item.id,
          editor: this.getEditorLabel(item.editor),
          editor_object: item.editor,
          request_type: item.custom_data?.title ?? "qr",
          type: item.request_type,
          data: this.decorateForData(item),
          data_tooltip: this.decorateForDataTooltip(item),
          request_id: item.request_uuid,
          status: item.display_status || item.request_status,
          display_status: item.display_status || item.request_status, // Fallback to request_status if display_status is missing
          status_tooltip: this.decorateForStatusTooltip(item),
          created_at: item.created_at,
          updated_at: item.updated_at,
          time: item.created_at,
          timestamp: new Date(item.created_at).getTime(),
          duration_tooltip: item.duration_tooltip,
          message: this.decorateForMessage(item),
          message_tooltip: this.decorateForMessageTooltip(item),
          custom_request_type: item.custom_data?.title,
          custom_data: item.custom_data,
          _item: item,
          _search: this.generateSearchString([item.request_data, item.decorated_response, item.extra_data, item.custom_data]),
        }
      })
      this.localItems = items
      this.dataTableItems = items
      this.editors = items.map(item => item.editor).filter( (item, index, array) => array.indexOf(item) === index)
    },
    decorateItems(results) {
      const newItems = results.data.data.list
      
      // Create a map of existing items for fast lookup
      const existingItemsMap = new Map(this.dataTableItems.map(item => [item.ID, item]))

      let items = newItems.map(item => {
        // Debug: Check if display_status exists
        if (item.request_status === 'requested' && !item.display_status) {
          console.warn('Missing display_status for requested item:', item)
        }
        
        const existingItem = existingItemsMap.get(item.id)

        // Calculate heavily computed fields
        const data = this.decorateForData(item)
        const data_tooltip = this.decorateForDataTooltip(item)
        const message = this.decorateForMessage(item)
        const message_tooltip = this.decorateForMessageTooltip(item)
        const status_tooltip = this.decorateForStatusTooltip(item)
        const search = this.generateSearchString([
          item.request_data, 
          item.decorated_response, 
          item.extra_data, 
          item.custom_data,
          data_tooltip,
          message_tooltip,
          status_tooltip
        ])

        if (existingItem) {
          // Merge into existing item to preserve reactivity and avoid full re-render
          // We update the properties of the existing object in-place
          existingItem.data = data
          existingItem.data_tooltip = data_tooltip
          existingItem.message = message
          existingItem.message_tooltip = message_tooltip
          existingItem.status_tooltip = status_tooltip
          existingItem._search = search
          
          // Also update other potentially changed fields
          existingItem.status = item.display_status || item.request_status
          existingItem.display_status = item.display_status || item.request_status
          existingItem.updated_at = item.updated_at
          existingItem.duration_tooltip = item.duration_tooltip
          existingItem._item = item // Update raw item reference

          return existingItem
        } else {
          // New item, creating from scratch
          return {
            ID: item.id,
            editor: this.getEditorLabel(item.editor),
            editor_object: item.editor,
            request_type: item.custom_data?.title ? item.custom_data.title : "qr",
            type: item.request_type,
            data: data,
            data_tooltip: data_tooltip,
            request_id: item.request_uuid,
            status: item.display_status || item.request_status,
            display_status: item.display_status || item.request_status, // Fallback to request_status if display_status is missing
            status_tooltip: status_tooltip,
            created_at: item.created_at,
            updated_at: item.updated_at,
            time: item.created_at,
            timestamp: new Date(item.created_at).getTime(),
            duration_tooltip: item.duration_tooltip,
            message: message,
            message_tooltip: message_tooltip,
            custom_request_type: item.custom_data?.title,
            custom_data: item.custom_data,
            _item: item,
            _search: search,
          }
        }
      })
      
      this.localItems = items
      this.dataTableItems = items
      this.editors = items.map(item => item.editor).filter( (item, index, array) => array.indexOf(item) === index)
    },
    decorateForData(item) {
      if (item.extra_data && item.extra_data.summary) {
        return item.extra_data.summary
      }

      const requestData = item.request_data || {}
      const requestList = Array.isArray(item.request_data) ? item.request_data : []
      const accounts = Array.isArray(requestData.accounts) ? requestData.accounts : []
      const links = Array.isArray(requestData.links) ? requestData.links : []
      const listItems = Array.isArray(requestData.list) ? requestData.list : []

      if (item.request_type === "PK_PASS") {
        return `${item.extra_data?.file_list?.length ?? 0} pkpass`
      } else if (this.refreshTypes.includes(item.request_type)) {
        if (accounts.length > 0) {
          return `${accounts.length} account(s)`
        } else if (links.length > 0) {
          return `${links.length} links`
        }
      } else if (item.request_type === "GENERATE_QR") {
        return `${requestList.length} tickets`
      } else if (item.request_type === "BALLPARK_VERIFY_EMAIL") {
        return requestData.account_user ?? "-"
      } else if (item.request_type === "CREATE_BALLPARK_ACCOUNT") {
        return requestData.account_user ?? "-"
      } else if (item.request_type === "RESET_ACCOUNT_PASSWORD") {
        return requestData.account_user ?? "-"
      } else if (item.request_type === "SIDE_DMS") {
        return `${links.length} links`
      } else if (item.request_type === "WEB_PRINT_TICKET") {
        return `${accounts.length} tickets`
      } else if (item.request_type === "GENERATE_CSV") {
        return `${listItems.length} tickets`
      } else if (item.request_type === "REFRESH_INVOICE") {
        return `${accounts.length} account(s)`
      }

      return "-"
    },
    decorateForDataTooltip(item) {
      if (item.extra_data && item.extra_data.tooltip) {
        return item.extra_data.tooltip.replace(/\r\n/g, '<br/>').replace(/\n/g, '<br/>')
      }

      const requestData = item.request_data || {}
      const requestList = Array.isArray(item.request_data) ? item.request_data : []
      const accounts = Array.isArray(requestData.accounts) ? requestData.accounts : []
      const links = Array.isArray(requestData.links) ? requestData.links : []
      const csvList = Array.isArray(requestData.list) ? requestData.list : []

      if (item.request_type === "PK_PASS") {
        let files = item.extra_data?.file_list?.length > 5 ? item.extra_data.file_list?.slice(0,5) : item.extra_data.file_list
        let addMore = item.extra_data?.file_list?.length > 5 ? "<br/>..." : ""
        return files.join("<br />") + addMore
      } else if (this.refreshTypes.includes(item.request_type)) {
        if (item.request_type === "PACIOLAN_SCRAPE") {
          // For Paciolan, show from_date - to_date in tooltip
          const requestData = item.request_data || {}
          if (requestData.from_time && requestData.to_time) {
            const fromDate = new Date(requestData.from_time * 1000)
            const toDate = new Date(requestData.to_time * 1000)
            const fromFormatted = fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            const toFormatted = toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            return `From: ${fromFormatted}<br/>To: ${toFormatted}`
          }
          return null
        }
        if (item.request_type === "SEATGEEK_OFFICIAL_API") {
          // For SeatGeek Official API, show from_date - to_date and account info in tooltip
          const requestData = item.request_data || {}
          const accounts = Array.isArray(requestData.accounts) ? requestData.accounts : []
          let tooltipParts = []
          
          // Add account info (performer_id)
          if (accounts.length > 0) {
            const accountInfo = accounts.map(account => account.account_performer || account.account_user || account.performer_id || 'Unknown').join("<br />")
            tooltipParts.push(accountInfo)
          }
          
          // Add date range
          if (requestData.from_time && requestData.to_time) {
            const fromDate = new Date(requestData.from_time * 1000)
            const toDate = new Date(requestData.to_time * 1000)
            const fromFormatted = fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            const toFormatted = toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            tooltipParts.push(`From: ${fromFormatted}<br/>To: ${toFormatted}`)
          }
          
          return tooltipParts.length > 0 ? tooltipParts.join("<br/>") : null
        }
        if (item.request_type === "SEATGEEK_SCRAPE") {
          // For SeatGeek Scrape (SeatGeek to QR V2), show from_date - to_date and account info in tooltip
          const requestData = item.request_data || {}
          const accounts = Array.isArray(requestData.accounts) ? requestData.accounts : []
          let tooltipParts = []
          
          // Add account info (performer_id)
          if (accounts.length > 0) {
            const accountInfo = accounts.map(account => account.account_performer || account.account_user || account.performer_id || 'Unknown').join("<br />")
            tooltipParts.push(accountInfo)
          }
          
          // Add date range
          if (requestData.from_time && requestData.to_time) {
            const fromDate = new Date(requestData.from_time * 1000)
            const toDate = new Date(requestData.to_time * 1000)
            const fromFormatted = fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            const toFormatted = toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            tooltipParts.push(`From: ${fromFormatted}<br/>To: ${toFormatted}`)
          }
          
          return tooltipParts.length > 0 ? tooltipParts.join("<br/>") : null
        }
        if (accounts.length > 0) {
          return accounts.map(account => account.account_user || account.account_performer).join("<br />")
        } else if (links.length > 0) {
          return links.join("<br />")
        }
      } else if (item.request_type === "GENERATE_QR") {
        let tickets = requestList.length > 5 ?  requestList.slice(0, 5) : requestList
        let addMore = requestList.length > 5 ? "<br/>..." : ""
        return tickets.map(ticket => [ticket.event, ticket.section, ticket.row, ticket.seat].join(", ")).join("<br/>") + addMore
      } else if (item.request_type === "GENERATE_CSV") {
        let list = csvList.length > 5 ? csvList.slice(0, 5) : csvList
        let addMore = csvList.length > 5 ? "<br/>..." : ""
        let tickets = camelizeKeys(list)

        return tickets.map(ticket => [ticket.event ?? ticket.teams, ticket.section, ticket.row, ticket.seat].join(", ")).join("<br/>") + addMore
      } else if (item.request_type === "BALLPARK_VERIFY_EMAIL") {
        return [requestData.account_user, requestData.email_to_be_verified].filter(Boolean).join(" -> ")
      } else if (item.request_type === "CREATE_BALLPARK_ACCOUNT") {
        return [requestData.account_user, requestData.account_name, requestData.account_surname].filter(Boolean).join(":")
      } else if (item.request_type === "SIDE_DMS") {
        return links.join("<br />")
      } else if (item.request_type === "REFRESH_INVOICE") {
        return accounts.map(account => account.account_user).join("<br />")
      } else if (item.request_type === 'WEB_PRINT_TICKET') {
        return accounts.map(account => account.account_user + " -> " + (account.account_tickets?.join(", ") || "")).join("<br />")
      }

      return null
    },
    decorateForMessage(item) {
      const requestData = item.request_data || {}
      const csvList = Array.isArray(requestData.list) ? requestData.list : []
      const decoratedResponseList = Array.isArray(item.decorated_response) ? item.decorated_response : []

      if (item.request_type === "GENERATE_QR" || item.request_type === "SEATGEEK_V2_GENERATE") {
        if (item.request_status === 'requested') {
          return ""
        }
        // Prioritize api_response_message if available
        if (item.api_response_message) {
            return item.api_response_message
        }
        const ticketCount = item.extra_data?.file_count ?? item.extra_data?.file_list?.length ?? 0
        return `Generated ${ticketCount} tickets`
      } else if (item.request_type === "PK_PASS") {
        if (item.request_status === 'requested') {
          return ""
        } else if (item.request_status === 'failed') {
          return "We couldn't generate any ticket."
        }
        let number = item.extra_data?.file_count ?? item.extra_data?.file_list?.length
        return `Generated ${number} tickets`
      } else if (item.request_type === "SIDE_DMS") {
        if (item.request_status === 'requested') {
          return ""
        }
        if (decoratedResponseList.length === 0) {
          return item.api_response_message ?? "We couldn't find pass"
        } else {
          return `Found ${decoratedResponseList.length} tickets`
        }
      } else if (this.refreshTypes.includes(item.request_type)) {
        if (item.request_type === "PACIOLAN_SCRAPE") {
          // For Paciolan, show ticket count
          const ticketCount = item.extra_data?.ticket_count || (Array.isArray(item.decorated_response) ? item.decorated_response.length : 0)
          if (ticketCount > 0) {
            return ticketCount === 1 ? '1 ticket' : `${ticketCount} ticket(s)`
          } else if (item.request_status === 'completed') {
            return 'No tickets found'
          } else if (item.request_status === 'failed') {
            return '0 tickets'
          }
          return item.api_response_message || ""
        }
        if (item.request_type === "SEATGEEK_OFFICIAL_API") {
          // For SeatGeek Official API, show ticket count
          const ticketCount = item.extra_data?.ticket_count || (Array.isArray(item.decorated_response) ? item.decorated_response.length : 0)
          if (ticketCount > 0) {
            return ticketCount === 1 ? '1 ticket' : `${ticketCount} ticket(s)`
          } else if (item.request_status === 'completed') {
            return 'No tickets found'
          } else if (item.request_status === 'failed') {
            return '-'
          }
          return item.api_response_message || ""
        }
        if (item.request_type === "SEATGEEK_SCRAPE") {
          // For SeatGeek Scrape (SeatGeek to QR v2), show ticket count
          const ticketCount = item.extra_data?.ticket_count || (Array.isArray(item.decorated_response) ? item.decorated_response.length : 0)
          if (ticketCount > 0) {
            return ticketCount === 1 ? '1 ticket' : `${ticketCount} ticket(s)`
          } else if (item.request_status === 'completed') {
            return 'No tickets found'
          } else if (item.request_status === 'failed') {
            return '-'
          }
          return item.api_response_message || ""
        }
        return item.api_response_message
      } else if (item.request_type === "BALLPARK_VERIFY_EMAIL") {
        return item.request_status === "completed" ? 'Account linked successfully' : "Can't linked"
      } else if (item.request_type === "CREATE_BALLPARK_ACCOUNT" || item.request_type === "RESET_ACCOUNT_PASSWORD") {
        if (!!item.decorated_response.response) {
          return item.decorated_response?.response?.slice(0, 20) + "..."
        }
        return ""
      } else if (item.request_type === "GENERATE_CSV") {
        if (item.request_status === "completed") {
          return `Created CSV file (${csvList.length} tickets)`
        } else {
          return "Not able to generate CSV file"
        }
      } else if (item.request_type === "GENERATE_QR_ASYNC") {
        return `Found ${decoratedResponseList.length} tickets`
      } else if (item.request_type === "REFRESH_INVOICE") {
        return item.api_response_message
      } else if (item.request_type === "WEB_PRINT_TICKET") {
        return item.api_response_message
      }

      return "-"
    },
    decorateForStatusTooltip(item) {
      // Success: show only request_id
      if (item.request_status === 'completed' || item.request_status === 'requested') {
        return item.request_uuid || ''
      }
      // Failed: show request_id - error message
      if (item.request_status === 'failed') {
        const errorMessage = item.api_response_message || 'Request failed'
        let tooltip = `${item.request_uuid || ''} - ${errorMessage}`
        
        // Add detailed errors from summary if available
        if (item.error_summary && item.error_summary.length > 0) {
           tooltip += "<br/><br/>" + item.error_summary.join("<br/>")
        }
        
        return tooltip
      }
      // Default: show request_id
      return item.request_uuid || ''
    },
    decorateForMessageTooltip(item) {
      // Allow tooltip for completed status for SEATGEEK_OFFICIAL_API and SEATGEEK_SCRAPE
      const allowCompletedTooltip = item.request_type === "SEATGEEK_OFFICIAL_API" || item.request_type === "SEATGEEK_SCRAPE"
      if (item.request_status !== "failed" && !allowCompletedTooltip) { return "" }

      const decoratedResponseList = Array.isArray(item.decorated_response) ? item.decorated_response : []
      const errorSummary = Array.isArray(item.error_summary) ? item.error_summary : []

      if (item.request_type === "CREATE_BALLPARK_ACCOUNT" || item.request_type === "RESET_ACCOUNT_PASSWORD") {
        return item.decorated_response.response || ""
      } else if (item.request_type === "SIDE_DMS") {
        if (item.request_status === 'requested') {
          return ""
        }
        return decoratedResponseList.map(event => event.event).join("<br/>")
      } else if(item.request_type === "REFRESH_CREDITS") {
        if (errorSummary.length > 0) {
          return errorSummary.join("<br />")
        } else {
          return decoratedResponseList.map(item => `${item.account_user} -> ${item.__account_credit_amount} ${item.__account_credit_currency}`).join("<br/>")
        }
      } else if (this.refreshTypes.includes(item.request_type)) {
        if (item.request_type === "PACIOLAN_SCRAPE") {
          // For Paciolan, show error messages in tooltip when failed
          if (item.request_status === 'failed') {
            if (errorSummary.length > 0) {
              return errorSummary.join("<br />")
            }
            // Fallback to api_response_message if no errors array
            return item.api_response_message || "Scrape operation failed"
          }
          return ""
        }
        if (item.request_type === "SEATGEEK_OFFICIAL_API") {
          // For SeatGeek Official API, show error messages in tooltip when failed
          if (item.request_status === 'failed') {
            if (errorSummary.length > 0) {
              return errorSummary.join("<br />")
            }
            // Fallback to api_response_message if no errors array
            return item.api_response_message || "Scrape operation failed"
          } else if (item.request_status === 'completed') {
            // For SeatGeek Official API, tickets have event_name field (not event_detail.__event_name)
            return decoratedResponseList.slice(0, 4).map(ticket => ticket.event_name || ticket.event).join("<br/>") + (decoratedResponseList.length > 4 ? "<br />..." : "")
          }
          return ""
        }
        if (item.request_type === "SEATGEEK_SCRAPE") {
          // For SeatGeek Scrape (SeatGeek to QR v2), show error messages in tooltip when failed
          if (item.request_status === 'failed') {
            if (errorSummary.length > 0) {
              return errorSummary.join("<br />")
            }
            // Fallback to api_response_message if no errors array
            return item.api_response_message || "Scrape operation failed"
          } else if (item.request_status === 'completed') {
            // For SeatGeek Scrape, show event names in tooltip
            return decoratedResponseList.slice(0, 4).map(event => event.event_detail?.__event_name || event.event_name || event.event).join("<br/>") + (decoratedResponseList.length > 4 ? "<br />..." : "")
          }
          return ""
        }
        if (item.request_status === 'failed') {
          if (errorSummary.length > 0) {
            return errorSummary.join("<br />")
          }
        } else if (item.request_status === 'completed') {
          return decoratedResponseList.slice(0, 4).map(event => event.event_detail?.__event_name).join("<br/>") + "<br />..."
        }
      } else if (item.request_type === "REFRESH_INVOICE") {
        if (item.request_status === 'failed') {
          if (errorSummary.length > 0) {
            return errorSummary.join("<br />")
          }
          return item.api_response_message
        } else if (item.request_status === 'completed') {
          return decoratedResponseList.map(item => `${item.invoice_descriptions}`).join("<br />")
        }
      } else if (item.request_type === "WEB_PRINT_TICKET") {
        if (errorSummary.length > 0) {
          return errorSummary.join("<br />")
        }
      } else if (item.request_type === 'PK_PASS') {
        return item.api_response_message ?? ""
      }

      return ""
    },
    fromNow(dateString) {
      if (!dateString) return ""
      return moment(dateString).fromNow()
    },
    getTimeTooltip(item) {
      const dateText = this.printableDate(item.time)
      const displayStatus = item.display_status || item.status
      
      // If status is abandoned, show special message instead of duration
      if (displayStatus === 'abandoned') {
        return `${dateText}<br/>Abandoned: Request not completed after 1 hour`
      }
      
      const durationInfo = item.duration_tooltip
      if (durationInfo) {
        return `${dateText}<br/>duration: ${durationInfo}`
      }
      return dateText
    },

    onDownloadClick(item) {
      this.downloadFile(item._item.extra_data?.file_name ?? item.request_id, item.request_id, item._item.request_type, false)
    },
    onCloakifyClick(item) {
      this.downloadFile(item._item.extra_data?.file_name, item.request_id, item._item.request_type, true)
    },
    downloadFile(fileName, requestId, requestType, cloakify) {
      this.response.message = null
      if (fileName === null || requestId === null) return

      this.alert.loading = true
      this.alert.message = "Please stand by"

      let callback = (percentage) => {
        if (percentage > 0) {
          this.percentage = percentage
          this.alert.loading = false
        } else if (percentage === 0) {
          this.alert.loading = true
        }
      }

      let request = [
        GenerateServices.download(requestId, requestType, callback),
      ]

      if (cloakify) {
        request = [
            GenerateServices.download(requestId + "_cloakify", requestType, callback)
        ]
      }

      Promise.all(request).then(responses => {
        responses.forEach( (response, index) => {
          let fn = !!cloakify ?  requestId + "_cloakify.zip" : (this.fileName ?? this.getFileName(fileName))
          let reader = new FileReader()
          reader.onload = () => {
            let type = requestType === 'GENERATE_CSV' ? 'text/csv' : 'application/zip'
            let blob = new Blob([response.data], {type})
            let url = window['URL'].createObjectURL(blob)
            let a = document.createElement('a')
            a.href = url
            a.download = fn
            a.click()
          }
          reader.readAsBinaryString(response.data)
        })
      }).catch(error => {
        if (error.networkError === 'function') {
          this.response = error.networkError?.(error)
        } else {
          this.response = this.returnCustomError(error.toString())
        }
      }).finally(() => {
        this.alert.loading = false
        this.alert.message = null
        this.percentage = null
      })
    },
    onGoToPageClick(item) {
      let query = {}

      if (item.status === "completed" && this.refreshTypes.includes(item.type)) {
        query = { history: item.request_id }
      }
      const fullPath = item._item?.custom_data?.full_path
      if (!fullPath) { return }
      let path = `${fullPath.split("?")[0]}`

      this.$router.push({ path, query })
    },
    onRetryClick(item) {
      // Ensure _item exists and has required data
      if (!item._item) {
        console.error('onRetryClick: _item is missing', item)
        return
      }
      
      const fullPath = item._item?.custom_data?.full_path
      if (!fullPath) {
        console.warn('onRetryClick: fullPath is missing', item)
        return
      }
      
      // Ensure request_type exists
      const requestType = item._item.request_type || item.type
      if (!requestType) {
        console.error('onRetryClick: request_type is missing', item)
        return
      }
      
      let path = `${fullPath.split("?")[0]}`
      let query = getQueryParams(requestType, item)

      this.$router.push({ path, query })
    },
  },
  mounted() {
    // Clean expired retry data on page load
    const { cleanExpiredRetryData } = require('@/util/retryStorage')
    cleanExpiredRetryData()
    
    // Check for permission error from router guard
    if (this.$route.query.permissionError === 'true') {
      // Remove query parameter from URL after component is mounted
      this.$nextTick(() => {
        this.$router.replace({ query: {} }).catch(() => {})
      })
    }
    
    // Only fetch data if there's no permission error
    if (!this.showPermissionError) {
      this.fetchLight()
    }
  },
}
</script>

<style scoped>
.editor-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
}

.editor-badge.head-editor {
  background-color: #e3f2fd;
  color: #1976d2;
}

.editor-badge.head-editor i {
  font-size: 14px;
}

.editor-badge.you-badge {
  background-color: #1976d2;
  color: #ffffff;
  font-weight: 600;
}

.editor-badge.sub-editor {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

.editor-badge.sub-editor i {
  font-size: 14px;
}
</style>
